import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Pager } from "../components/Pager";
import { Card, Chips, ErrorBanner, fmt } from "../components/ui";
import { api, type ActivityPage } from "../lib/api";

type Kind = "changes" | "purchases" | "moves";

const str = (v: unknown) => (v === null || v === undefined || v === "" ? "—" : String(v));

function details(d: unknown): string {
  if (!d || typeof d !== "object") return "";
  return Object.entries(d as Record<string, unknown>)
    .filter(([, v]) => v !== null && v !== undefined && typeof v !== "object")
    .map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`)
    .join(" · ")
    .slice(0, 220);
}

/** What happened and who did it: every change made in the console or through
 *  Claude, every purchase the customer webhook recorded, and every move the
 *  journey rules made. All of it was recorded; none of it had a screen. */
export function Activity() {
  const [kind, setKind] = useState<Kind>("changes");
  const [days, setDays] = useState(7);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [data, setData] = useState<ActivityPage | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setPage(1), [kind, days, perPage]);
  useEffect(() => {
    api
      .activity(kind, days, page, perPage)
      .then((r) => {
        setData(r);
        setError(null);
      })
      .catch((e) => setError(e.message));
  }, [kind, days, page, perPage]);

  return (
    <>
      <ErrorBanner error={error} />
      <div className="row w">
        <h2 style={{ fontSize: 19 }}>Activity</h2>
        <div className="sp" />
        <Chips<Kind>
          value={kind}
          onChange={setKind}
          options={[
            { key: "changes", label: "Who changed what" },
            { key: "purchases", label: "Purchases" },
            { key: "moves", label: "Journey moves" },
          ]}
        />
        <select className="f" value={days} onChange={(e) => setDays(Number(e.target.value))}>
          {[1, 7, 30, 90].map((d) => (
            <option key={d} value={d}>
              last {d} day{d > 1 ? "s" : ""}
            </option>
          ))}
        </select>
      </div>

      <Card>
        {!data ? (
          <div className="empty">Loading…</div>
        ) : data.items.length === 0 ? (
          <div className="empty">Nothing in this period.</div>
        ) : (
          <div className="tw">
            <table>
              <thead>
                {kind === "changes" ? (
                  <tr><th>When</th><th>Who</th><th>What</th><th>Details</th></tr>
                ) : kind === "purchases" ? (
                  <tr><th>When</th><th>Person</th><th>Event</th><th>Plan</th><th style={{ textAlign: "right" }}>Amount</th><th>Left drips</th></tr>
                ) : (
                  <tr><th>When</th><th>Person</th><th>What happened</th></tr>
                )}
              </thead>
              <tbody>
                {data.items.map((r, i) => (
                  <tr key={i}>
                    <td className="mono" style={{ whiteSpace: "nowrap" }}>{fmt.et(str(r.at))}</td>
                    {kind === "changes" ? (
                      <>
                        <td>{str(r.who)}</td>
                        <td>
                          <b>{str(r.action)}</b>
                          {r.what ? <span className="dim"> · {str(r.what)}</span> : null}
                        </td>
                        <td className="dim" style={{ fontSize: 12 }}>{details(r.details)}</td>
                      </>
                    ) : (
                      <>
                        <td>
                          {r.contact_id ? (
                            <Link to={`/contacts/${r.contact_id}`}>{str(r.name ?? r.phone ?? r.contact_id)}</Link>
                          ) : (
                            "—"
                          )}
                        </td>
                        {kind === "purchases" ? (
                          <>
                            <td>{str(r.event).replace(/_/g, " ")}</td>
                            <td>{str(r.plan)}</td>
                            <td className="mono" style={{ textAlign: "right" }}>
                              {r.amount ? `$${Number(r.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "—"}
                            </td>
                            <td className="mono">{str(r.left_campaigns)}</td>
                          </>
                        ) : (
                          <td>{str(r.change)}</td>
                        )}
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data && (
          <Pager page={data.page} pages={data.pages} total={data.total} perPage={perPage} onPage={setPage} onPerPage={setPerPage} />
        )}
      </Card>
    </>
  );
}
